import {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import api from '../../Redux/Axios/middleware';

interface LevelCalibration {
  meters_per_pixel?: number | null;
  origin_px?: { x: number | null; y: number | null } | null;
  rotation_deg?: number | null;
  image_width_px?: number | null;
  image_height_px?: number | null;
}

interface FixtureLike {
  _id: string;
  fixture_id: string | number;
  beacon?: {
    x_m?: number | null;
    y_m?: number | null;
    heading_deg?: number | null;
    measured_power_dbm?: number | null;
  } | null;
  level_id?: {
    _id?: string;
    level_diagram?: string;
    diagram_calibration?: LevelCalibration;
  } | null;
}

interface FixtureBeaconModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  fixture: FixtureLike | null;
  allFixtures?: FixtureLike[];
}

const MAX_WIDTH = 720;

function metricToPixel(
  xm: number,
  ym: number,
  cal: LevelCalibration
): { x: number; y: number } | null {
  const mpp = Number(cal?.meters_per_pixel);
  const ox = Number(cal?.origin_px?.x);
  const oy = Number(cal?.origin_px?.y);
  const theta = ((Number(cal?.rotation_deg) || 0) * Math.PI) / 180;
  if (!Number.isFinite(mpp) || mpp <= 0) return null;
  if (!Number.isFinite(ox) || !Number.isFinite(oy)) return null;
  const dxPx = (xm * Math.cos(theta) - ym * Math.sin(theta)) / mpp;
  const dyPx = (xm * Math.sin(theta) + ym * Math.cos(theta)) / mpp;
  return { x: ox + dxPx, y: oy + dyPx };
}

function pixelToMetric(
  px: number,
  py: number,
  cal: LevelCalibration
): { x_m: number; y_m: number } | null {
  const mpp = Number(cal?.meters_per_pixel);
  const ox = Number(cal?.origin_px?.x);
  const oy = Number(cal?.origin_px?.y);
  const theta = ((Number(cal?.rotation_deg) || 0) * Math.PI) / 180;
  if (!Number.isFinite(mpp) || mpp <= 0) return null;
  if (!Number.isFinite(ox) || !Number.isFinite(oy)) return null;
  const dxPx = px - ox;
  const dyPx = py - oy;
  // Rotate by -theta then scale by mpp.
  const x_m = (dxPx * Math.cos(theta) + dyPx * Math.sin(theta)) * mpp;
  const y_m = (-dxPx * Math.sin(theta) + dyPx * Math.cos(theta)) * mpp;
  return { x_m, y_m };
}

const FixtureBeaconModal: FunctionComponent<FixtureBeaconModalProps> = ({
  open,
  onClose,
  onSaved,
  fixture,
  allFixtures,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [displayed, setDisplayed] = useState<{ w: number; h: number } | null>(
    null
  );
  const [pointPx, setPointPx] = useState<{ x: number; y: number } | null>(null);
  const [hoverDisp, setHoverDisp] = useState<{ x: number; y: number } | null>(
    null
  );
  const [measuredPower, setMeasuredPower] = useState<string>('');
  const [headingDeg, setHeadingDeg] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  const calibration = fixture?.level_id?.diagram_calibration ?? null;
  const diagramPath = fixture?.level_id?.level_diagram ?? null;
  const diagramSrc = useMemo(() => {
    if (!diagramPath) return null;
    return `${process.env.REACT_APP_API_URL}/${diagramPath}`;
  }, [diagramPath]);

  const calibrated =
    calibration != null &&
    calibration.meters_per_pixel != null &&
    Number.isFinite(Number(calibration.meters_per_pixel));

  // Seed from the fixture's existing placement when the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoadError(false);
    setNatural(null);
    setDisplayed(null);
    setHoverDisp(null);
    const b = fixture?.beacon;
    if (
      b &&
      calibration &&
      calibrated &&
      b.x_m != null &&
      b.y_m != null &&
      Number.isFinite(Number(b.x_m)) &&
      Number.isFinite(Number(b.y_m))
    ) {
      const px = metricToPixel(Number(b.x_m), Number(b.y_m), calibration);
      setPointPx(px);
    } else {
      setPointPx(null);
    }
    setMeasuredPower(
      b?.measured_power_dbm != null ? String(b.measured_power_dbm) : ''
    );
    setHeadingDeg(b?.heading_deg != null ? String(b.heading_deg) : '');
    // Only reset on open/fixture switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixture?._id]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img || !natural) return;
    const ro = new ResizeObserver(() => {
      setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    });
    ro.observe(img);
    return () => ro.disconnect();
  }, [natural, open]);

  const handleLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    setLoadError(false);
  };
  const handleImgError = () => {
    setLoadError(true);
    setNatural(null);
    setDisplayed(null);
  };

  const handleMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!natural || !calibrated) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverDisp({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const handleLeave = () => setHoverDisp(null);

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!natural || submitting || !calibrated) return;
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const naturalX = Math.round((clickX / rect.width) * natural.w);
    const naturalY = Math.round((clickY / rect.height) * natural.h);
    const clamp = (v: number, max: number) => Math.max(0, Math.min(max - 1, v));
    setPointPx({ x: clamp(naturalX, natural.w), y: clamp(naturalY, natural.h) });
  };

  const metric = useMemo(() => {
    if (!pointPx || !calibration || !calibrated) return null;
    return pixelToMetric(pointPx.x, pointPx.y, calibration);
  }, [pointPx, calibration, calibrated]);

  const toDisplayFromNatural = (
    pt: { x: number; y: number } | null
  ): { left: number; top: number } | null => {
    if (!pt || !natural || !displayed) return null;
    return {
      left: (pt.x / natural.w) * displayed.w,
      top: (pt.y / natural.h) * displayed.h,
    };
  };

  const currentDisp = toDisplayFromNatural(pointPx);

  const originDisp = useMemo(() => {
    const ox = calibration?.origin_px?.x;
    const oy = calibration?.origin_px?.y;
    if (ox == null || oy == null) return null;
    return toDisplayFromNatural({ x: Number(ox), y: Number(oy) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calibration, natural, displayed]);

  // Reference pins for every other placed fixture on this level.
  const referencePins = useMemo(() => {
    if (!calibration || !calibrated || !allFixtures) return [];
    const out: {
      key: string;
      label: string;
      left: number;
      top: number;
    }[] = [];
    for (const f of allFixtures) {
      if (!f || f._id === fixture?._id) continue;
      const b = f.beacon;
      if (!b || b.x_m == null || b.y_m == null) continue;
      const xm = Number(b.x_m);
      const ym = Number(b.y_m);
      if (!Number.isFinite(xm) || !Number.isFinite(ym)) continue;
      const px = metricToPixel(xm, ym, calibration);
      if (!px) continue;
      const disp = toDisplayFromNatural(px);
      if (!disp) continue;
      out.push({
        key: String(f._id),
        label: `${f.fixture_id} (${Math.round(px.x)}, ${Math.round(px.y)}) px`,
        left: disp.left,
        top: disp.top,
      });
    }
    return out;
    // toDisplayFromNatural depends on natural/displayed; recompute when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFixtures, fixture?._id, calibration, calibrated, natural, displayed]);

  const validation = useMemo(() => {
    const errs: string[] = [];
    if (measuredPower !== '') {
      const m = Number(measuredPower);
      if (!Number.isInteger(m) || m < -128 || m > 127) {
        errs.push('measured_power_dbm must be an integer in [-128, 127]');
      }
    }
    if (headingDeg !== '') {
      const h = Number(headingDeg);
      if (!Number.isFinite(h) || h < 0 || h >= 360) {
        errs.push('heading_deg must be in [0, 360)');
      }
    }
    return errs;
  }, [measuredPower, headingDeg]);

  const canSave =
    Boolean(fixture?._id) &&
    calibrated &&
    Boolean(metric) &&
    validation.length === 0 &&
    !submitting;

  const handleSave = async () => {
    if (!fixture?._id || !metric) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        x_m: metric.x_m,
        y_m: metric.y_m,
      };
      if (measuredPower !== '') body.measured_power_dbm = Number(measuredPower);
      else body.measured_power_dbm = null;
      if (headingDeg !== '') body.heading_deg = Number(headingDeg);
      else body.heading_deg = null;
      await api.put(`/fixtures/${fixture._id}/beacon`, body);
      onSaved?.();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      const reason =
        data?.reason ??
        (typeof data?.response === 'string' ? data.response : null) ??
        data?.responseMessage ??
        err?.message ??
        'Request failed';
      setError(
        reason === 'level_not_calibrated'
          ? 'This level is not calibrated. Calibrate the level first, then place the fixture.'
          : typeof reason === 'string'
            ? reason
            : JSON.stringify(reason)
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Place fixture{' '}
        {fixture?.fixture_id ? `"${fixture.fixture_id}"` : ''} on map
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 8 }}>
          Click the floor plan to set this fixture's position. The click is
          converted to metric (x_m, y_m) using the level's calibration.
        </div>

        {!diagramSrc ? (
          <Alert severity="warning">
            The owning level has no diagram image. Upload a level_diagram
            first.
          </Alert>
        ) : !calibrated ? (
          <Alert severity="warning">
            This level is not calibrated. Calibrate the level first — the
            map click can't be converted to meters without a scale.
          </Alert>
        ) : loadError ? (
          <Alert severity="error">Failed to load the diagram image.</Alert>
        ) : (
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: MAX_WIDTH,
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              overflow: 'hidden',
              background: '#f3f4f6',
              cursor: submitting ? 'not-allowed' : 'crosshair',
            }}>
            <img
              ref={imgRef}
              src={diagramSrc}
              alt="level diagram"
              onLoad={handleLoad}
              onError={handleImgError}
              onClick={handleClick}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
              draggable={false}
              style={{
                display: 'block',
                width: '100%',
                maxWidth: MAX_WIDTH,
                height: 'auto',
                userSelect: 'none',
              }}
            />
            {hoverDisp && displayed && natural && calibrated ? (
              <>
                <svg
                  width={displayed.w}
                  height={displayed.h}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    pointerEvents: 'none',
                  }}>
                  <line
                    x1={0}
                    y1={hoverDisp.y}
                    x2={displayed.w}
                    y2={hoverDisp.y}
                    stroke="#2563eb"
                    strokeWidth={1}
                    strokeDasharray="4 3"
                    opacity={0.7}
                  />
                  <line
                    x1={hoverDisp.x}
                    y1={0}
                    x2={hoverDisp.x}
                    y2={displayed.h}
                    stroke="#2563eb"
                    strokeWidth={1}
                    strokeDasharray="4 3"
                    opacity={0.7}
                  />
                </svg>
                <div
                  style={{
                    position: 'absolute',
                    left: hoverDisp.x + 10,
                    top: hoverDisp.y - 20,
                    padding: '1px 5px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    background: '#2563eb',
                    borderRadius: 2,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}>
                  {`(${Math.round((hoverDisp.x / displayed.w) * natural.w)}, ${Math.round((hoverDisp.y / displayed.h) * natural.h)}) px`}
                </div>
              </>
            ) : null}
            {originDisp ? (
              <div
                style={{
                  position: 'absolute',
                  left: originDisp.left - 10,
                  top: originDisp.top - 10,
                  width: 20,
                  height: 20,
                  pointerEvents: 'none',
                }}>
                <svg width={20} height={20} style={{ overflow: 'visible' }}>
                  <circle cx={10} cy={10} r={7} fill="none" stroke="#f59e0b" strokeWidth={2} />
                  <line x1={10} y1={3} x2={10} y2={17} stroke="#f59e0b" strokeWidth={2} />
                  <line x1={3} y1={10} x2={17} y2={10} stroke="#f59e0b" strokeWidth={2} />
                </svg>
                <div style={{
                  position: 'absolute',
                  left: 14,
                  top: -14,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                  background: '#f59e0b',
                  borderRadius: 2,
                  padding: '0 4px',
                  whiteSpace: 'nowrap',
                }}>origin (0, 0)</div>
              </div>
            ) : null}
            {referencePins.map((p) => (
              <Pin
                key={p.key}
                left={p.left}
                top={p.top}
                label={p.label}
                color="#6b7280"
                size={14}
              />
            ))}
            {currentDisp ? (
              <Pin
                left={currentDisp.left}
                top={currentDisp.top}
                label={
                  pointPx
                    ? `${fixture?.fixture_id ?? '•'} (${pointPx.x}, ${pointPx.y}) px`
                    : String(fixture?.fixture_id ?? '•')
                }
                color="#dc2626"
                size={18}
              />
            ) : null}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 14,
          }}>
          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
            <div>
              <b>Pixel:</b>{' '}
              {pointPx
                ? `(${pointPx.x}, ${pointPx.y}) px`
                : '— click the diagram'}
            </div>
            <div>
              <b>Metric:</b>{' '}
              {metric
                ? `x_m=${metric.x_m.toFixed(3)}, y_m=${metric.y_m.toFixed(3)}`
                : '—'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <TextField
              label="measured_power_dbm (optional)"
              type="number"
              size="small"
              value={measuredPower}
              onChange={(e) => setMeasuredPower(e.target.value)}
              inputProps={{ min: -128, max: 127, step: 1 }}
              disabled={submitting || !calibrated}
              style={{ maxWidth: 240 }}
              helperText="int in [-128, 127] or blank"
            />
            <TextField
              label="heading_deg (optional)"
              type="number"
              size="small"
              value={headingDeg}
              onChange={(e) => setHeadingDeg(e.target.value)}
              inputProps={{ min: 0, max: 359.9, step: 0.1 }}
              disabled={submitting || !calibrated}
              style={{ maxWidth: 240 }}
              helperText="deg in [0, 360) or blank"
            />
          </div>

          {validation.length > 0 ? (
            <Alert severity="warning">{validation.join(' / ')}</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setPointPx(null);
            setMeasuredPower('');
            setHeadingDeg('');
            setError(null);
          }}
          disabled={submitting || !pointPx}>
          Reset
        </Button>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Saving…' : 'Save placement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Pin: FunctionComponent<{
  left: number;
  top: number;
  label: string;
  color: string;
  size?: number;
}> = ({ left, top, label, color, size = 16 }) => {
  const r = size / 2;
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: left - r,
          top: top - r,
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          border: '2px solid #fff',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: left + r + 2,
          top: top - r - 10,
          padding: '0 4px',
          fontSize: 11,
          fontWeight: 700,
          color: '#fff',
          background: color,
          borderRadius: 2,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
        {label}
      </div>
    </>
  );
};

export default FixtureBeaconModal;
